import type { CreateClient } from '@wf-coe/types';
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from 'flowbite-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useCreateClientMutation } from '../../queries/use-create-client.js';

interface ClientEditModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the newly-created client id so the parent can navigate. */
  onCreated?: (id: string) => void;
}

/**
 * Modal-based create form for a new tenant. Replaces the previous
 * inline form on the Clients page so it matches the legacy
 * `wf-adminconsole/components/clients/ClientEditModal.tsx` UX.
 *
 * Today the form covers `name` + `email`; the legacy edit modal also
 * surfaced logos and GitHub fields, but those are out of scope for the
 * onboarding step and will land with the per-tenant Settings page in a
 * later slice.
 */
export function ClientEditModal({ open, onClose, onCreated }: ClientEditModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const mutation = useCreateClientMutation();

  // Reset form state every time the modal opens. Otherwise a previous
  // failed submission's input + error would carry across to the next open.
  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      mutation.reset();
    }
    // mutation.reset is stable; intentionally omit from deps so re-renders
    // mid-edit don't blow away in-progress input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const input: CreateClient = {
      name: name.trim(),
      email: email.trim() === '' ? null : email.trim(),
      smallLogoUrl: null,
      largeLogoUrl: null,
      githubRepoName: null,
      githubRepoOwner: null,
      githubBranch: null,
    };
    mutation.mutate(input, {
      onSuccess: (created) => {
        onCreated?.(created.id);
        onClose();
      },
    });
  };

  return (
    <Modal show={open} onClose={onClose} dismissible>
      <ModalHeader>New client</ModalHeader>
      <form onSubmit={handleSubmit} aria-label="Create new client">
        <ModalBody>
          <div className="space-y-4">
            <div>
              <div className="mb-1 block">
                <Label htmlFor="client-name">
                  Name <span className="text-red-500">*</span>
                </Label>
              </div>
              <TextInput
                id="client-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={mutation.isPending}
              />
            </div>

            <div>
              <div className="mb-1 block">
                <Label htmlFor="client-email">Email</Label>
              </div>
              <TextInput
                id="client-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={mutation.isPending}
              />
            </div>

            {mutation.isError ? (
              <div
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
              >
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : 'Failed to create client'}
              </div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending || name.trim() === ''}>
            {mutation.isPending ? 'Creating…' : 'Create client'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
